import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { API_BASE_URL } from '@/lib/api-client';
import { ProjectLayoutSections } from '@/features/project-layout/ProjectLayoutSections';
import type { ProjectLayoutData } from '@/features/project-layout/types';

/**
 * Sprint 41 W34 — public customer-facing proposal view, rendered from
 * the immutable EstimateSnapshot payload captured at finalize time
 * (W33). The URL token is the bearer: no JWT, no auth header. The
 * backend collapses unknown / expired / revoked tokens into a single
 * 404 so URL guessers can't distinguish "doesn't exist" from
 * "was-once-valid."
 *
 * Mounted at `/p/:token`. The legacy `/proposal/:proposalId` route
 * now lives on `LegacyProposalRedirect`, which bounces through the
 * W35-A backend mapping into this view (W35-cutover).
 *
 * Renders through `<ProjectLayoutSections mode="proposal">`, which per
 * §14.S12-Q3 + the W15 capability matrix shows only Sections II
 * (ProjectSummary) + III (ProjectScope) + VI (InvestmentSummary).
 * Sections IV (ContractTerms) and V (Warranty) are intentionally
 * hidden — proposals are a discussion artifact, not a binding one;
 * binding terms surface on the Contract page (S12).
 */

interface ProposalSnapshotMeta {
  id: string;
  versionIndex: number;
  publicToken: string;
  publicTokenExpiresAt: string;
  createdAt: string;
  revokedAt: string | null;
}

interface ProposalSnapshotResponse {
  snapshot: ProposalSnapshotMeta;
  // Captured ResolvedEstimateDto JSON — loose because the snapshot is
  // immutable while the DTO shape may evolve. The umbrella consumes
  // `ProjectLayoutData` which is itself a loose shape (`unknown`-typed
  // children); the cast below is the same one EstimatePage / Review
  // already use.
  payload: ProjectLayoutData;
}

async function fetchProposalByToken(
  token: string,
): Promise<ProposalSnapshotResponse> {
  const response = await fetch(`${API_BASE_URL}/proposal/${token}`);
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as {
      message?: string;
    };
    throw new Error(err.message || 'Proposal not found.');
  }
  return response.json() as Promise<ProposalSnapshotResponse>;
}

export default function ProposalPublicView() {
  const { token } = useParams<{ token: string }>();

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery<ProposalSnapshotResponse, Error>({
    queryKey: ['proposal-snapshot', token],
    queryFn: () => fetchProposalByToken(token!),
    enabled: Boolean(token),
    // The snapshot is immutable; cache aggressively per browser session.
    staleTime: Infinity,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading proposal…</p>
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="font-newsreader text-xl font-semibold tracking-tight text-foreground mb-2">
              Proposal not available
            </h2>
            <p className="text-sm text-muted-foreground">
              {error?.message ||
                "This proposal link may have expired or been revoked. Please contact your sales rep for a fresh link."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
        <ProjectLayoutSections
          data={{
            ...data.payload,
            project: {
              ...(data.payload.project ?? { id: '' }),
              allowances:
                data.payload.project?.allowances ??
                (data.payload as { allowances?: unknown }).allowances ??
                null,
            },
          }}
          mode="proposal"
        />
      </div>
    </div>
  );
}
